import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Truck, MapPin, ArrowRight, RefreshCw, AlertTriangle, CheckCircle, Clock, Layers, GitBranch, ChevronDown, ChevronRight, BarChart3, ShoppingCart, Box, Zap } from 'lucide-react';
import { authService } from '../services/auth';
import apiClient from '../services/api';
import { toast } from 'sonner';

const MODES = {
  ship_from_dc: { label: 'Ship from DC', color: '#3EF1C7', icon: '🏭' },
  ship_from_store: { label: 'Ship from Store', color: '#7A5CFF', icon: '🏪' },
  bopis: { label: 'BOPIS', color: '#FFC857', icon: '🛍️' },
  ropis: { label: 'ROPIS', color: '#FFC857', icon: '📋' },
  drop_ship: { label: 'Drop Ship', color: '#F55454', icon: '📦' },
  store_transfer: { label: 'Store Transfer', color: '#9CB0C7', icon: '🔄' },
  cross_dock: { label: 'Cross Dock', color: '#9CB0C7', icon: '🔀' },
};

const STATUS_COLORS = {
  created: '#9CB0C7', picking: '#FFC857', packed: '#7A5CFF', labeled: '#7A5CFF',
  shipped: '#3EF1C7', in_transit: '#3EF1C7', out_for_delivery: '#3EF1C7',
  delivered: '#3EF1C7', exception: '#F55454', pending: '#9CB0C7',
  allocated: '#7A5CFF', backordered: '#F55454', cancelled: '#F55454',
};

export default function OrderOrchestrationPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('pipeline');
  const [orders, setOrders] = useState([]);
  const [shippingOrders, setShippingOrders] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [ordRes, anaRes] = await Promise.all([
        apiClient.get('/api/v1/orders'),
        apiClient.get('/api/v1/orchestration/analytics'),
      ]);
      setOrders(ordRes.data.orders || []);
      setAnalytics(anaRes.data);
    } catch {
      // Demo data
      setOrders([
        { order_id: 'ord_a1b2c3', confirmation_number: 'CI-7F2A9B31', status: 'allocated', total: 189.99, customer: { email: 'sarah@example.com', first_name: 'Sarah' }, items: [{ title: 'Wireless Headphones', quantity: 1, sku: 'WH-100-BLK' }, { title: 'USB-C Cable 3pk', quantity: 2, sku: 'CB-USB3-WHT' }], shipping_order_count: 1, split_shipment: false, channel: 'web', created_at: '2026-03-06T10:00:00Z', fulfillment_mode: 'ship_from_dc' },
        { order_id: 'ord_d4e5f6', confirmation_number: 'CI-3C8D1E45', status: 'partially_allocated', total: 342.50, customer: { email: 'mike@example.com', first_name: 'Mike' }, items: [{ title: 'Running Shoes Ultra', quantity: 1, sku: 'RS-ULT-10' }, { title: 'Athletic Socks 3pk', quantity: 1, sku: 'SK-ATH-3PK' }, { title: 'Water Bottle Pro', quantity: 1, sku: 'WB-PRO-32' }], shipping_order_count: 2, split_shipment: true, channel: 'mobile', created_at: '2026-03-06T09:30:00Z', fulfillment_mode: 'ship_from_store' },
        { order_id: 'ord_g7h8i9', confirmation_number: 'CI-9A4F7B22', status: 'confirmed', total: 79.00, customer: { email: 'agent-7x9k@moltbook.com', first_name: 'Agent-7X9K' }, items: [{ title: 'Smart Sensor Kit', quantity: 1, sku: 'SS-KIT-V2' }], shipping_order_count: 0, split_shipment: false, channel: 'agent', created_at: '2026-03-06T11:15:00Z', fulfillment_mode: null },
        { order_id: 'ord_j0k1l2', confirmation_number: 'CI-5E2B8C67', status: 'allocated', total: 1249.00, customer: { email: 'lisa@example.com', first_name: 'Lisa' }, items: [{ title: 'Smart Home Hub Pro', quantity: 3, sku: 'SH-HUB-PRO' }], shipping_order_count: 1, split_shipment: false, channel: 'web', created_at: '2026-03-05T16:00:00Z', fulfillment_mode: 'bopis', pickup_location: 'Store #42 - San Francisco' },
      ]);
      setShippingOrders([
        { shipping_order_id: 'so_abc123', order_id: 'ord_a1b2c3', node_id: 'loc_warehouse_a', node_type: 'warehouse', fulfillment_mode: 'ship_from_dc', status: 'picking', lines: [{ sku: 'WH-100-BLK', quantity: 1 }, { sku: 'CB-USB3-WHT', quantity: 2 }], priority: 'standard', carrier: null },
        { shipping_order_id: 'so_def456', order_id: 'ord_d4e5f6', node_id: 'loc_store_sf', node_type: 'store', fulfillment_mode: 'ship_from_store', status: 'created', lines: [{ sku: 'RS-ULT-10', quantity: 1 }], priority: 'express', carrier: null },
        { shipping_order_id: 'so_ghi789', order_id: 'ord_d4e5f6', node_id: 'loc_warehouse_b', node_type: 'warehouse', fulfillment_mode: 'ship_from_dc', status: 'packed', lines: [{ sku: 'SK-ATH-3PK', quantity: 1 }, { sku: 'WB-PRO-32', quantity: 1 }], priority: 'express', carrier: 'UPS' },
      ]);
      setAnalytics({
        total_orders_orchestrated: 847,
        total_shipping_orders: 1023,
        split_order_rate: 18.4,
        avg_shipping_orders_per_order: 1.21,
        fulfillment_mode_distribution: { ship_from_dc: 612, ship_from_store: 198, bopis: 134, drop_ship: 56, ropis: 23 },
        shipping_order_status_distribution: { delivered: 689, shipped: 134, packed: 67, picking: 45, created: 88 },
      });
    } finally {
      setLoading(false);
    }
  };

  const orchestrateOrder = async (orderId) => {
    try {
      await apiClient.post('/api/v1/orchestration/orchestrate', { order_id: orderId });
      toast.success('Order orchestrated');
      loadData();
    } catch { toast.error('Orchestration failed'); }
  };

  const advanceShippingOrder = async (soId, newStatus) => {
    try {
      await apiClient.post(`/api/v1/orchestration/shipping-order/${soId}/advance?new_status=${newStatus}`);
      toast.success(`Shipping order advanced to ${newStatus}`);
      loadData();
    } catch { toast.error('Failed to advance'); }
  };

  const stats = analytics ? [
    { label: 'Orders Orchestrated', value: analytics.total_orders_orchestrated, icon: ShoppingCart, color: '#3EF1C7' },
    { label: 'Shipping Orders', value: analytics.total_shipping_orders, icon: Truck, color: '#7A5CFF' },
    { label: 'Split Rate', value: `${analytics.split_order_rate}%`, icon: GitBranch, color: '#FFC857' },
    { label: 'Avg SO/Order', value: analytics.avg_shipping_orders_per_order, icon: Layers, color: '#9CB0C7' },
  ] : [];

  const modeData = analytics?.fulfillment_mode_distribution || {};
  const maxMode = Math.max(...Object.values(modeData), 1);

  return (
    <div className="min-h-screen" style={{ background: '#0B1120' }}>
      <header className="border-b border-[#22304A] bg-[#0E1426]/80 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Order Orchestration</h1>
            <p className="text-sm text-[#9CB0C7]">Lines → Allocation → Shipping Orders → Fulfillment</p>
          </div>
          <div className="flex items-center gap-3">
            {['pipeline', 'shipping', 'analytics'].map(t => (
              <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 rounded-lg text-sm font-medium transition ${tab === t ? 'bg-[#7A5CFF] text-white' : 'border border-[#22304A] text-[#9CB0C7] hover:text-white'}`}>
                {t === 'pipeline' ? 'Order Pipeline' : t === 'shipping' ? 'Shipping Orders' : 'Analytics'}
              </button>
            ))}
            <button onClick={() => navigate('/dashboard')} className="px-4 py-2 border border-[#22304A] text-[#9CB0C7] rounded-lg hover:text-white transition">Dashboard</button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        {analytics && (
          <div className="grid grid-cols-4 gap-4 mb-6">
            {stats.map((s, i) => (
              <div key={i} className="bg-[#0E1426] border border-[#22304A] rounded-xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <s.icon className="w-4 h-4" style={{ color: s.color }} />
                  <span className="text-xs text-[#9CB0C7]">{s.label}</span>
                </div>
                <div className="text-2xl font-bold text-white">{s.value}</div>
              </div>
            ))}
          </div>
        )}

        {/* Tab: Order Pipeline */}
        {tab === 'pipeline' && (
          <div className="space-y-4">
            {orders.map(order => (
              <div key={order.order_id} className="bg-[#0E1426] border border-[#22304A] rounded-xl overflow-hidden">
                <div className="p-4 flex items-center justify-between cursor-pointer" onClick={() => setExpandedOrder(expandedOrder === order.order_id ? null : order.order_id)}>
                  <div className="flex items-center gap-4">
                    <div>
                      <span className="text-sm text-[#7A5CFF] font-mono">{order.confirmation_number}</span>
                      <div className="text-white font-medium">{order.customer?.first_name || 'Customer'}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 rounded text-[10px] font-medium" style={{ background: `${STATUS_COLORS[order.status]}15`, color: STATUS_COLORS[order.status] }}>
                        {order.status?.replace('_', ' ')}
                      </span>
                      {order.channel === 'agent' && <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-[#FFC857]/10 text-[#FFC857]">Agent</span>}
                      {order.split_shipment && <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-[#F55454]/10 text-[#F55454]">Split</span>}
                      {order.fulfillment_mode && (
                        <span className="px-2 py-0.5 rounded text-[10px] font-medium" style={{ background: `${MODES[order.fulfillment_mode]?.color || '#9CB0C7'}15`, color: MODES[order.fulfillment_mode]?.color }}>
                          {MODES[order.fulfillment_mode]?.icon} {MODES[order.fulfillment_mode]?.label}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-white font-medium">${order.total?.toFixed(2)}</span>
                    <span className="text-xs text-[#9CB0C7]">{order.items?.length} items</span>
                    {order.shipping_order_count > 0 && <span className="text-xs text-[#9CB0C7]">{order.shipping_order_count} SO</span>}
                    {!order.fulfillment_mode && order.status === 'confirmed' && (
                      <button onClick={e => { e.stopPropagation(); orchestrateOrder(order.order_id); }} className="px-3 py-1.5 bg-[#7A5CFF] text-white rounded-lg text-xs font-medium flex items-center gap-1">
                        <Zap className="w-3 h-3" /> Orchestrate
                      </button>
                    )}
                    {expandedOrder === order.order_id ? <ChevronDown className="w-4 h-4 text-[#9CB0C7]" /> : <ChevronRight className="w-4 h-4 text-[#9CB0C7]" />}
                  </div>
                </div>

                {expandedOrder === order.order_id && (
                  <div className="border-t border-[#22304A] p-4">
                    <div className="text-xs text-[#9CB0C7] mb-3 font-medium">ORDER LINES</div>
                    <div className="space-y-2 mb-4">
                      {order.items?.map((item, i) => (
                        <div key={i} className="flex items-center justify-between bg-[#0B1120] rounded-lg p-3">
                          <div className="flex items-center gap-3">
                            <Package className="w-4 h-4 text-[#7A5CFF]" />
                            <div>
                              <div className="text-sm text-white">{item.title}</div>
                              <div className="text-xs text-[#9CB0C7] font-mono">{item.sku}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="text-xs text-[#9CB0C7]">Qty: {item.quantity}</span>
                            <span className="px-2 py-0.5 rounded text-[10px] font-medium" style={{ background: `${STATUS_COLORS[order.status]}15`, color: STATUS_COLORS[order.status] }}>
                              {order.status === 'allocated' ? 'Allocated' : order.status === 'confirmed' ? 'Pending Source' : order.status?.replace('_', ' ')}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Shipping Orders for this order */}
                    {shippingOrders.filter(so => so.order_id === order.order_id).length > 0 && (
                      <>
                        <div className="text-xs text-[#9CB0C7] mb-3 font-medium">SHIPPING ORDERS</div>
                        <div className="space-y-2">
                          {shippingOrders.filter(so => so.order_id === order.order_id).map(so => {
                            const mode = MODES[so.fulfillment_mode] || {};
                            const nextMap = { created: 'picking', picking: 'packed', packed: 'labeled', labeled: 'shipped', shipped: 'in_transit', in_transit: 'out_for_delivery', out_for_delivery: 'delivered' };
                            const next = nextMap[so.status];
                            return (
                              <div key={so.shipping_order_id} className="bg-[#0B1120] rounded-lg p-3">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs text-[#9CB0C7] font-mono">{so.shipping_order_id}</span>
                                    <span className="px-2 py-0.5 rounded text-[10px] font-medium" style={{ background: `${mode.color || '#9CB0C7'}15`, color: mode.color }}>
                                      {mode.icon} {mode.label}
                                    </span>
                                    <span className="px-2 py-0.5 rounded text-[10px] font-medium" style={{ background: `${STATUS_COLORS[so.status]}15`, color: STATUS_COLORS[so.status] }}>
                                      {so.status}
                                    </span>
                                    {so.priority === 'express' && <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-[#FFC857]/10 text-[#FFC857]">Express</span>}
                                  </div>
                                  {next && (
                                    <button onClick={() => advanceShippingOrder(so.shipping_order_id, next)} className="px-2 py-1 bg-[#7A5CFF]/10 text-[#7A5CFF] rounded text-xs hover:bg-[#7A5CFF]/20 transition flex items-center gap-1">
                                      <ArrowRight className="w-3 h-3" /> {next}
                                    </button>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 text-xs text-[#9CB0C7]">
                                  <MapPin className="w-3 h-3" /> {so.node_id} ({so.node_type})
                                  <span className="mx-1">|</span>
                                  {so.lines.map((l, li) => <span key={li}>{l.sku} ×{l.quantity}</span>)}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Tab: Shipping Orders */}
        {tab === 'shipping' && (
          <div className="bg-[#0E1426] border border-[#22304A] rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#22304A]">
                  <th className="text-left p-4 text-xs text-[#9CB0C7] font-medium">Shipping Order</th>
                  <th className="text-left p-4 text-xs text-[#9CB0C7] font-medium">Order</th>
                  <th className="text-left p-4 text-xs text-[#9CB0C7] font-medium">Node</th>
                  <th className="text-left p-4 text-xs text-[#9CB0C7] font-medium">Mode</th>
                  <th className="text-center p-4 text-xs text-[#9CB0C7] font-medium">Lines</th>
                  <th className="text-center p-4 text-xs text-[#9CB0C7] font-medium">Status</th>
                  <th className="text-right p-4 text-xs text-[#9CB0C7] font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {shippingOrders.map(so => {
                  const mode = MODES[so.fulfillment_mode] || {};
                  const nextMap = { created: 'picking', picking: 'packed', packed: 'labeled', labeled: 'shipped' };
                  const next = nextMap[so.status];
                  return (
                    <tr key={so.shipping_order_id} className="border-b border-[#22304A]/50 hover:bg-[#7A5CFF]/5">
                      <td className="p-4 text-sm text-white font-mono">{so.shipping_order_id}</td>
                      <td className="p-4 text-sm text-[#7A5CFF] font-mono">{so.order_id}</td>
                      <td className="p-4 text-xs text-[#9CB0C7]"><MapPin className="w-3 h-3 inline mr-1" />{so.node_id}</td>
                      <td className="p-4"><span className="px-2 py-0.5 rounded text-[10px] font-medium" style={{ background: `${mode.color}15`, color: mode.color }}>{mode.icon} {mode.label}</span></td>
                      <td className="p-4 text-center text-sm text-white">{so.lines?.length}</td>
                      <td className="p-4 text-center"><span className="px-2 py-0.5 rounded text-[10px] font-medium" style={{ background: `${STATUS_COLORS[so.status]}15`, color: STATUS_COLORS[so.status] }}>{so.status}</span></td>
                      <td className="p-4 text-right">
                        {next && <button onClick={() => advanceShippingOrder(so.shipping_order_id, next)} className="px-2 py-1 bg-[#7A5CFF]/10 text-[#7A5CFF] rounded text-xs hover:bg-[#7A5CFF]/20">→ {next}</button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Tab: Analytics */}
        {tab === 'analytics' && analytics && (
          <div className="space-y-6">
            <div className="text-sm text-[#9CB0C7] mb-2 font-medium">FULFILLMENT MODE DISTRIBUTION</div>
            <div className="bg-[#0E1426] border border-[#22304A] rounded-xl p-6">
              <div className="space-y-4">
                {Object.entries(modeData).map(([mode, count]) => {
                  const m = MODES[mode] || { label: mode, color: '#9CB0C7' };
                  return (
                    <div key={mode} className="flex items-center gap-4">
                      <div className="w-32 text-sm text-white">{m.icon || ''} {m.label}</div>
                      <div className="flex-1 h-6 bg-[#0B1120] rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${(count / maxMode) * 100}%`, background: m.color }} />
                      </div>
                      <div className="w-16 text-right text-sm font-bold" style={{ color: m.color }}>{count}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="bg-[#0E1426] border border-[#22304A] rounded-xl p-6">
                <div className="text-sm text-[#9CB0C7] mb-4 font-medium">OMNICHANNEL BREAKDOWN</div>
                <div className="space-y-3">
                  {[
                    { label: 'Ship from DC', desc: 'Standard warehouse fulfillment', pct: 60 },
                    { label: 'Ship from Store', desc: 'Store acts as mini-DC', pct: 19 },
                    { label: 'BOPIS', desc: 'Buy Online Pickup In Store', pct: 13 },
                    { label: 'Drop Ship', desc: 'Direct vendor → customer', pct: 5 },
                    { label: 'ROPIS', desc: 'Reserve Online Pickup In Store', pct: 3 },
                  ].map((item, i) => (
                    <div key={i}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-white">{item.label}</span>
                        <span className="text-xs text-[#9CB0C7]">{item.pct}%</span>
                      </div>
                      <div className="text-xs text-[#9CB0C7] mb-1">{item.desc}</div>
                      <div className="h-1.5 bg-[#0B1120] rounded-full"><div className="h-full bg-[#7A5CFF] rounded-full" style={{ width: `${item.pct}%` }} /></div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-[#0E1426] border border-[#22304A] rounded-xl p-6">
                <div className="text-sm text-[#9CB0C7] mb-4 font-medium">INVENTORY → ORDER INTEGRATION POINTS</div>
                <div className="space-y-2 text-xs">
                  {[
                    'Cart → Inventory Check (real-time ATP)',
                    'Checkout → Reserve (TTL-based soft hold)',
                    'Payment Auth → Hard Reservation',
                    'Order Confirm → Fraud Score + Hold Check',
                    'Decompose → Line-level allocation',
                    'Source → Multi-node evaluation (12 rules)',
                    'Allocate → Inventory decrement at node',
                    'Split → Multi-SO creation per node',
                    'Pick → Committed inventory scan',
                    'Pack → Weight/dims capture',
                    'Label → Carrier rate shop + selection',
                    'Ship → Payment capture + settlement',
                    'In Transit → Tracking aggregation',
                    'Deliver → WISMO notification',
                    'Return → Inspect → Restock/Dispose',
                    'Settlement → Platform fee + merchant payout',
                  ].map((point, i) => (
                    <div key={i} className="flex items-center gap-2 text-[#9CB0C7]">
                      <CheckCircle className="w-3 h-3 text-[#3EF1C7] flex-shrink-0" />
                      {point}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
